import React, { useCallback, useEffect, useMemo } from 'react';

import { ReportToolTip } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { ViewOptionsBar } from 'components/ViewOptionsBar/ViewOptionsBar';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { orderByComparator, SortDescriptor } from 'utils/dataQuery';
import { Card, Col, Container, Row, Table } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { ReportSection } from 'apis/ReportApi';
import { buildExportFileName } from 'utils';
import { useSuperState } from 'hooks';
import { CSVLink } from 'react-csv';

import 'pages/reports/components/Report73/Report73.scss';

import {
    calculateHourMinFormat,
    EXPANDED_COLUMN_WIDTH,
    formatDate,
    sortableHeader
} from '../ReportFunctions';
import { createSortChangeHandler, GridSortChangeEvent } from '../sortUtils';

// ============= TYPES =============

interface Report73Props {
    mode?: string;
    reportSections: ReportSection[];
}

interface ObjectiveOption {
    name: string;
    value: string;
}

type ViewMode = 'Student' | 'Question';
type MetricView = 'Attained' | 'Recent' | 'Attempts' | 'Time';

interface SummaryMetrics {
    avgAttained: number;
    avgAttemptsPerStudent: number;
    avgCoverage: number;
    avgRecent: number;
    avgTimePerQuestionMinutes: number;
    studentsAttemptedPercent: number;
}

interface StudentRow {
    attained: number;
    avgAttemptsPerQuestion: number;
    avgTimePerQuestionMinutes: number;
    Classroom: string | null;
    coverage: number;
    recent: number;
    StudentName: string;
    StudentProfileId: number;
}

interface QuestionRow {
    attained: number;
    avgAttemptsPerStudent: number;
    avgTimePerAttemptMinutes: number;
    displayName: string;
    objectives: string[];
    QuestionId: number;
    recent: number;
    studentCoverage: number;
}

// ============= CONSTANTS =============

const STUDENT_DETAIL_REPORT_ID = '72';
const MAX_DISPLAY_ROWS = 500;

// ============= HELPERS =============

const isCorrect = (attempt: any): boolean => attempt.Correct?.Share === '1';

const getQuestionDisplayName = (attempt: any): string => {

    const questionName = attempt.QuestionName || `Question ${attempt.QuestionId}`;
    const projectName = attempt.ProjectName;
    return projectName
        ? `${projectName} ${questionName} (${attempt.QuestionId})`
        : `${questionName} (${attempt.QuestionId})`;

};

const navigateToStudentDetail = (studentProfileId: number) => {

    const url = new URL(window.location.href);
    url.pathname = `/reports/${STUDENT_DETAIL_REPORT_ID}`;
    url.searchParams.set('ClassroomStudents', studentProfileId.toString());
    window.location.href = url.toString();

};

const formatSeconds = (minutes: number): string => {

    const totalSeconds = Math.round(minutes * 60);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    return calculateHourMinFormat(minutes);

};

/**
 * Render a check/x chip for attained/recent binary correctness.
 * Per PRD: 100% partial score = correct, anything < 100% = incorrect.
 */
const renderCorrectnessChip = (correct: boolean | null): React.ReactElement => {

    if (correct === null) {

        return (
            <span className="correctness-chip correctness-chip--na">
                --
            </span>
        );

    }
    if (correct) {

        return (
            <span className="correctness-chip correctness-chip--correct">
                <i className="fa-solid fa-check" />
            </span>
        );

    }
    return (
        <span className="correctness-chip correctness-chip--incorrect">
            <i className="fa-solid fa-xmark" />
        </span>
    );

};

// ============= COMPONENT =============

export const Report73 = ({
    mode = 'Testing',
    reportSections
}: Report73Props) => {

    // State
    const [state, setState] = useSuperState({
        exportFileName: 'TestProgressReport.csv',
        exportRows: [] as any[],
        metricView: 'Attained' as MetricView,
        objectiveOptions: [] as ObjectiveOption[],
        selectedObjectives: [] as ObjectiveOption[],
        showClassroomColumn: false,
        sort: [] as SortDescriptor[],
        viewMode: 'Student' as ViewMode
    });

    const { exportFileName, exportRows, metricView, objectiveOptions, selectedObjectives, showClassroomColumn, sort, viewMode } = state;

    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    const allData: any[] = reportSections[0]?.Components[0]?.Results?.Data ?? [];
    const rawData: any[] = useMemo(() => allData.filter((d: any) => !d.ContentAccessTypeDisplayName || d.ContentAccessTypeDisplayName === mode), [allData, mode]);

    // ---- Derived data: all unique question IDs, students, questions ----

    const allQuestionIds = useMemo(() => new Set(rawData.map((d: any) => d.QuestionId)), [rawData]);
    const totalQuestionsInScope = allQuestionIds.size;

    // Group raw attempts by student
    const studentAttemptsMap = useMemo(() => {

        const map: Record<number, any[]> = {};
        rawData.forEach((item: any) => {

            const id = item.StudentId;
            if (!map[id]) map[id] = [];
            map[id].push(item);

        });
        return map;

    }, [rawData]);

    const totalStudentsInScope = Object.keys(studentAttemptsMap).length;

    // Group raw attempts by question
    const questionAttemptsMap = useMemo(() => {

        const map: Record<number, any[]> = {};
        rawData.forEach((item: any) => {

            const qId = item.QuestionId;
            if (!map[qId]) map[qId] = [];
            map[qId].push(item);

        });
        return map;

    }, [rawData]);

    // Unique students list for dynamic columns in question view
    const uniqueStudents = useMemo(() => {

        const map = new Map<number, string>();
        rawData.forEach((item: any) => {

            if (!map.has(item.StudentId)) {

                const name = `${(item.FirstName || '').trim()} ${(item.LastName || '').trim()}`.trim();
                map.set(item.StudentId, name);

            }

        });
        return Array.from(map.entries()).map(([id, name]) => ({ StudentName: name, StudentProfileId: id }));

    }, [rawData]);

    // Unique questions list for dynamic columns in student view
    const allQuestions = useMemo(() => {

        const map = new Map<number, { displayName: string; objectives: string[]; QuestionId: number }>();
        rawData.forEach((a: any) => {

            if (!map.has(a.QuestionId)) {

                map.set(a.QuestionId, {
                    displayName: getQuestionDisplayName(a),
                    objectives: [],
                    QuestionId: a.QuestionId
                });

            }
            if (a.ObjectiveName) {

                const existing = map.get(a.QuestionId)!;
                if (!existing.objectives.includes(a.ObjectiveName)) {

                    existing.objectives.push(a.ObjectiveName);

                }

            }

        });
        return Array.from(map.values());

    }, [rawData]);

    // ---- Build a cell-level lookup: [studentId][questionId] -> aggregated metrics ----
    const cellData = useMemo(() => {

        const cells: Record<number, Record<number, {
            attained: boolean;
            attempts: number;
            lastAttemptDate: Date | null;
            recentCorrect: boolean | null;
            totalTimeMinutes: number;
        }>> = {};

        rawData.forEach((a: any) => {

            const sId = a.StudentId;
            const qId = a.QuestionId;
            if (!cells[sId]) cells[sId] = {};
            if (!cells[sId][qId]) {

                cells[sId][qId] = {
                    attained: false,
                    attempts: 0,
                    lastAttemptDate: null,
                    recentCorrect: null,
                    totalTimeMinutes: 0
                };

            }
            const cell = cells[sId][qId];
            cell.attempts += 1;
            cell.totalTimeMinutes += (a.Testtotalminutesspent || 0);

            if (isCorrect(a)) cell.attained = true;

            const ts = a.TimeStamp ? new Date(a.TimeStamp) : null;
            if (ts && (!cell.lastAttemptDate || ts > cell.lastAttemptDate)) {

                cell.lastAttemptDate = ts;
                cell.recentCorrect = isCorrect(a);

            }

        });

        return cells;

    }, [rawData]);

    // ---- Summary metrics (always from full dataset) ----
    const summaryMetrics = useMemo<SummaryMetrics>(() => {

        if (rawData.length === 0 || totalStudentsInScope === 0) {

            return {
                avgAttained: 0,
                avgAttemptsPerStudent: 0,
                avgCoverage: 0,
                avgRecent: 0,
                avgTimePerQuestionMinutes: 0,
                studentsAttemptedPercent: 0
            };

        }

        // Students attempted: at least one attempt
        const studentsAttempted = Object.keys(studentAttemptsMap).length;

        // All students in scope = those who appear in data. Everyone who appears has at least one attempt.
        const studentsAttemptedPercent = totalStudentsInScope > 0
            ? (studentsAttempted / totalStudentsInScope) * 100
            : 0;

        // Avg attempts per student
        const avgAttemptsPerStudent = totalStudentsInScope > 0
            ? rawData.length / totalStudentsInScope
            : 0;

        // Per-student coverage, attained, recent
        let totalCoverage = 0;
        let totalAttained = 0;
        let totalRecent = 0;

        Object.keys(studentAttemptsMap).forEach(sIdStr => {

            const sId = Number(sIdStr);
            const studentCells = cellData[sId] || {};
            const questionsAttempted = Object.keys(studentCells).length;
            const questionsAttained = Object.values(studentCells).filter(c => c.attained).length;
            const questionsRecentCorrect = Object.values(studentCells).filter(c => c.recentCorrect === true).length;

            totalCoverage += totalQuestionsInScope > 0 ? (questionsAttempted / totalQuestionsInScope) * 100 : 0;
            totalAttained += totalQuestionsInScope > 0 ? (questionsAttained / totalQuestionsInScope) * 100 : 0;
            totalRecent += totalQuestionsInScope > 0 ? (questionsRecentCorrect / totalQuestionsInScope) * 100 : 0;

        });

        const avgCoverage = totalStudentsInScope > 0 ? totalCoverage / totalStudentsInScope : 0;
        const avgAttained = totalStudentsInScope > 0 ? totalAttained / totalStudentsInScope : 0;
        const avgRecent = totalStudentsInScope > 0 ? totalRecent / totalStudentsInScope : 0;

        // Avg time per question attempt
        const totalAttempts = rawData.length;
        const totalTime = rawData.reduce((sum: number, a: any) => sum + (a.Testtotalminutesspent || 0), 0);
        const avgTimePerQuestionMinutes = totalAttempts > 0 ? totalTime / totalAttempts : 0;

        return {
            avgAttained,
            avgAttemptsPerStudent,
            avgCoverage,
            avgRecent,
            avgTimePerQuestionMinutes,
            studentsAttemptedPercent
        };

    }, [rawData, studentAttemptsMap, cellData, totalQuestionsInScope, totalStudentsInScope]);

    // ---- Objective options ----
    useEffect(() => {

        const objMap = new Map<string, string>();
        rawData.forEach((item: any) => {

            if (item.ObjectiveName) objMap.set(item.ObjectiveName, item.ObjectiveName);

        });
        const options: ObjectiveOption[] = Array.from(objMap.keys())
            .map(name => ({ name, value: name }))
            .sort((a, b) => a.name.localeCompare(b.name));
        setState({ objectiveOptions: options });
        setState({ selectedObjectives: options });

    }, [rawData]);

    // ---- Classroom column detection ----
    useEffect(() => {

        const classroomFilter = (reportSections[0] as any)?.ReportFilters?.find((filter: any) => filter.Name?.toLowerCase().includes('classroom') || filter.DisplayName?.toLowerCase().includes('classroom')
        );
        const hasMultipleClassrooms = classroomFilter?.Value?.includes(',') ?? false;
        setState({ showClassroomColumn: hasMultipleClassrooms });

    }, [reportSections]);

    // ---- Filtered questions based on selected objectives (visual only) ----
    const filteredQuestions = useMemo(() => {

        if (objectiveOptions.length === 0) return allQuestions;
        if (selectedObjectives.length === 0) return [];
        if (selectedObjectives.length === objectiveOptions.length) return allQuestions;
        const selectedValues = new Set(selectedObjectives.map(o => o.value));
        return allQuestions.filter(q => q.objectives.some(obj => selectedValues.has(obj)));

    }, [allQuestions, selectedObjectives, objectiveOptions]);

    const filteredQuestionIds = useMemo(
        () => new Set(filteredQuestions.map(q => q.QuestionId)),
        [filteredQuestions]
    );

    // ---- Student View rows ----
    const studentRows = useMemo<StudentRow[]>(() => Object.entries(studentAttemptsMap).map(([, attempts]) => {

        const sId = attempts[0].StudentId;
        const studentName = `${(attempts[0].FirstName || '').trim()} ${(attempts[0].LastName || '').trim()}`.trim();
        const studentCells = cellData[sId] || {};

        const questionsAttempted = Object.keys(studentCells).length;
        const questionsAttained = Object.values(studentCells).filter(c => c.attained).length;
        const questionsRecentCorrect = Object.values(studentCells).filter(c => c.recentCorrect === true).length;

        const coverage = totalQuestionsInScope > 0 ? (questionsAttempted / totalQuestionsInScope) * 100 : 0;
        const attained = totalQuestionsInScope > 0 ? (questionsAttained / totalQuestionsInScope) * 100 : 0;
        const recent = totalQuestionsInScope > 0 ? (questionsRecentCorrect / totalQuestionsInScope) * 100 : 0;

        const totalAttempts = attempts.length;
        const avgAttemptsPerQuestion = totalQuestionsInScope > 0 ? totalAttempts / totalQuestionsInScope : 0;

        const totalTime = attempts.reduce((sum: number, a: any) => sum + (a.Testtotalminutesspent || 0), 0);
        const avgTimePerQuestionMinutes = totalAttempts > 0 ? totalTime / totalAttempts : 0;

        return {
            attained,
            avgAttemptsPerQuestion,
            avgTimePerQuestionMinutes,
            Classroom: attempts[0].Classroom || null,
            coverage,
            recent,
            StudentName: studentName,
            StudentProfileId: sId
        };

    }), [studentAttemptsMap, cellData, totalQuestionsInScope]);

    // ---- Question View rows ----
    const questionRows = useMemo<QuestionRow[]>(() => allQuestions.map(q => {

        const attempts = questionAttemptsMap[q.QuestionId] || [];

        // Students who attempted this question
        const studentIdsWhoAttempted = new Set(attempts.map((a: any) => a.StudentId));
        const studentCoverage = totalStudentsInScope > 0
            ? (studentIdsWhoAttempted.size / totalStudentsInScope) * 100
            : 0;

        // Attained: students who answered correctly at least once
        const studentsAttained = new Set(
            attempts.filter((a: any) => isCorrect(a)).map((a: any) => a.StudentId)
        );
        const attained = totalStudentsInScope > 0
            ? (studentsAttained.size / totalStudentsInScope) * 100
            : 0;

        // Recent: for each student, check if their most recent attempt was correct
        const studentLatestMap: Record<number, any> = {};
        attempts.forEach((a: any) => {

            const sId = a.StudentId;
            if (!studentLatestMap[sId] || new Date(a.TimeStamp) > new Date(studentLatestMap[sId].TimeStamp)) {

                studentLatestMap[sId] = a;

            }

        });
        const recentCorrectStudents = Object.values(studentLatestMap).filter((a: any) => isCorrect(a)).length;
        const recent = totalStudentsInScope > 0
            ? (recentCorrectStudents / totalStudentsInScope) * 100
            : 0;

        // Avg attempts per student (among those who attempted)
        const avgAttemptsPerStudent = studentIdsWhoAttempted.size > 0
            ? attempts.length / studentIdsWhoAttempted.size
            : 0;

        // Avg time per attempt
        const totalTime = attempts.reduce((sum: number, a: any) => sum + (a.Testtotalminutesspent || 0), 0);
        const avgTimePerAttemptMinutes = attempts.length > 0 ? totalTime / attempts.length : 0;

        return {
            attained,
            avgAttemptsPerStudent,
            avgTimePerAttemptMinutes,
            displayName: q.displayName,
            objectives: q.objectives,
            QuestionId: q.QuestionId,
            recent,
            studentCoverage
        };

    }), [allQuestions, questionAttemptsMap, totalStudentsInScope]);

    // ---- Filtered question rows for question view ----
    const filteredQuestionRows = useMemo(() => {

        if (objectiveOptions.length === 0) return questionRows;
        if (selectedObjectives.length === 0) return [];
        if (selectedObjectives.length === objectiveOptions.length) return questionRows;
        const selectedValues = new Set(selectedObjectives.map(o => o.value));
        return questionRows.filter(q => q.objectives.some(obj => selectedValues.has(obj)));

    }, [questionRows, selectedObjectives, objectiveOptions]);

    // ---- Get cell metric value for display ----
    const getCellValue = useCallback((studentId: number, questionId: number): React.ReactElement | string => {

        const cell = cellData[studentId]?.[questionId];
        if (!cell) {

            switch (metricView) {

                case 'Attained':
                case 'Recent':
                    return renderCorrectnessChip(null);
                case 'Attempts':
                    return '0';
                case 'Time':
                    return '--';

            }

        }

        switch (metricView) {

            case 'Attained':
                return renderCorrectnessChip(cell.attained);
            case 'Recent':
                return renderCorrectnessChip(cell.recentCorrect);
            case 'Attempts':
                return cell.attempts.toString();
            case 'Time':
                return cell.attempts > 0 ? formatSeconds(cell.totalTimeMinutes / cell.attempts) : '--';

        }

    }, [cellData, metricView]);

    // ---- Cell tooltip ----
    const getCellTooltip = useCallback((studentId: number, questionId: number): React.ReactElement => {

        const cell = cellData[studentId]?.[questionId];
        if (!cell) {

            return (
                <p style={{ margin: 0, textAlign: 'start' }}>
                    <FormattedMessage defaultMessage="No attempts" id="no_attempts.e100b23a" />
                </p>
            );

        }

        return (
            <p style={{ margin: 0, textAlign: 'start' }}>
                <FormattedMessage defaultMessage="Total attempts: {count}" id="total_attempts_count.1732396e" values={{ count: cell.attempts }} />
                <br />
                <FormattedMessage defaultMessage="Attained: {value}" id="attained_value.f58b5b35" values={{ value: cell.attained ? formatMessage({ id: 'yes.03cb5fb3', defaultMessage: 'Yes' }) : formatMessage({ id: 'no.511a5d61', defaultMessage: 'No' }) }} />
                <br />
                <FormattedMessage defaultMessage="Recent: {value}" id="recent_value.505acccd" values={{ value: cell.recentCorrect === null ? '--' : cell.recentCorrect ? formatMessage({ id: 'correct.d6100cc2', defaultMessage: 'Correct' }) : formatMessage({ id: 'incorrect.2a901341', defaultMessage: 'Incorrect' }) }} />
                <br />
                <FormattedMessage defaultMessage="Avg time / attempt: {value}" id="avg_time_attempt_value.1aab44da" values={{ value: cell.attempts > 0 ? formatSeconds(cell.totalTimeMinutes / cell.attempts) : '--' }} />
                <br />
                <FormattedMessage defaultMessage="Last attempt: {value}" id="last_attempt_value.3a337ad9" values={{ value: cell.lastAttemptDate ? formatDate(cell.lastAttemptDate) : '--' }} />
            </p>
        );

    }, [cellData, formatMessage]);

    // ---- Sorting ----
    const getSortableValue = useCallback((item: any, field?: string) => {

        if (!field) return null;

        if (field.startsWith('question-')) {

            const qId = Number(field.replace('question-', ''));
            const cell = cellData[item.StudentProfileId]?.[qId];
            if (!cell) return -1;
            switch (metricView) {

                case 'Attained': return cell.attained ? 1 : 0;
                case 'Recent': return cell.recentCorrect ? 1 : 0;
                case 'Attempts': return cell.attempts;
                case 'Time': return cell.attempts > 0 ? cell.totalTimeMinutes / cell.attempts : -1;

            }

        }
        if (field.startsWith('student-')) {

            const sId = Number(field.replace('student-', ''));
            const cell = cellData[sId]?.[item.QuestionId];
            if (!cell) return -1;
            switch (metricView) {

                case 'Attained': return cell.attained ? 1 : 0;
                case 'Recent': return cell.recentCorrect ? 1 : 0;
                case 'Attempts': return cell.attempts;
                case 'Time': return cell.attempts > 0 ? cell.totalTimeMinutes / cell.attempts : -1;

            }

        }

        return item[field] ?? null;

    }, [cellData, metricView]);

    const handleSortChange = useCallback((event: GridSortChangeEvent) => {

        createSortChangeHandler(event, sort, val => setState({ sort: val }), getSortableValue);

    }, [sort, getSortableValue]);

    // Which data to sort/display
    const activeRows: any[] = viewMode === 'Student' ? studentRows : filteredQuestionRows;
    const sortedData = useMemo(() => orderByComparator(activeRows, sort, getSortableValue), [activeRows, sort, getSortableValue]);
    const displayData = useMemo(() => sortedData.slice(0, MAX_DISPLAY_ROWS), [sortedData]);

    // ---- Toast for truncated rows ----
    useEffect(() => {

        if (activeRows.length > MAX_DISPLAY_ROWS) {

            createToast(
                formatMessage(
                    { id: 'showing_first_shown_records_of_total_ple.18a484b7', defaultMessage: 'Showing first {shown} records of {total}. Please refine your filters to narrow the results.' },
                    { shown: MAX_DISPLAY_ROWS, total: activeRows.length }
                ),
                'warning'
            );

        }

    }, [activeRows]);

    // ---- Export ----
    const buildStudentViewExport = useCallback(() => sortedData.map((row: any) => {

        const base: Record<string, string | number> = {
            [formatMessage({ id: 'attained.704661b2', defaultMessage: 'Attained' })]: `${Math.round(row.attained)}%`,
            [formatMessage({ id: 'avg_attempts_question.64637c60', defaultMessage: 'Avg Attempts / Question' })]: row.avgAttemptsPerQuestion.toFixed(1),
            [formatMessage({ id: 'avg_time_question.1a5ce3ba', defaultMessage: 'Avg Time / Question' })]: formatSeconds(row.avgTimePerQuestionMinutes),
            [formatMessage({ id: 'question_coverage.47d009b6', defaultMessage: 'Question Coverage' })]: `${Math.round(row.coverage)}%`,
            [formatMessage({ id: 'recent.f6fba114', defaultMessage: 'Recent' })]: `${Math.round(row.recent)}%`,
            [formatMessage({ id: 'student_name.f4347b21', defaultMessage: 'Student Name' })]: row.StudentName
        };
        if (showClassroomColumn) base[formatMessage({ id: 'classroom.127e40cb', defaultMessage: 'Classroom' })] = row.Classroom || '---';
        filteredQuestions.forEach(q => {

            const cell = cellData[row.StudentProfileId]?.[q.QuestionId];
            switch (metricView) {

                case 'Attained':
                    base[q.displayName] = cell ? (cell.attained ? formatMessage({ id: 'yes.03cb5fb3', defaultMessage: 'Yes' }) : formatMessage({ id: 'no.511a5d61', defaultMessage: 'No' })) : '--';
                    break;
                case 'Recent':
                    base[q.displayName] = cell ? (cell.recentCorrect ? formatMessage({ id: 'correct.d6100cc2', defaultMessage: 'Correct' }) : formatMessage({ id: 'incorrect.2a901341', defaultMessage: 'Incorrect' })) : '--';
                    break;
                case 'Attempts':
                    base[q.displayName] = cell ? cell.attempts : 0;
                    break;
                case 'Time':
                    base[q.displayName] = cell && cell.attempts > 0 ? formatSeconds(cell.totalTimeMinutes / cell.attempts) : '--';
                    break;

            }

        });
        return base;

    }), [sortedData, filteredQuestions, cellData, metricView, showClassroomColumn, formatMessage]);

    const buildQuestionViewExport = useCallback(() => sortedData.map((row: any) => {

        const base: Record<string, string | number> = {
            [formatMessage({ id: 'attained.704661b2', defaultMessage: 'Attained' })]: `${Math.round(row.attained)}%`,
            [formatMessage({ id: 'avg_attempts_student.f3df6812', defaultMessage: 'Avg Attempts / Student' })]: row.avgAttemptsPerStudent.toFixed(1),
            [formatMessage({ id: 'avg_time_attempt.c6fd1ca6', defaultMessage: 'Avg Time / Attempt' })]: formatSeconds(row.avgTimePerAttemptMinutes),
            [formatMessage({ id: 'question.e7d7defa', defaultMessage: 'Question' })]: row.displayName,
            [formatMessage({ id: 'recent.f6fba114', defaultMessage: 'Recent' })]: `${Math.round(row.recent)}%`,
            [formatMessage({ id: 'student_coverage.92274580', defaultMessage: 'Student Coverage' })]: `${Math.round(row.studentCoverage)}%`
        };
        uniqueStudents.forEach(s => {

            const cell = cellData[s.StudentProfileId]?.[row.QuestionId];
            switch (metricView) {

                case 'Attained':
                    base[s.StudentName] = cell ? (cell.attained ? formatMessage({ id: 'yes.03cb5fb3', defaultMessage: 'Yes' }) : formatMessage({ id: 'no.511a5d61', defaultMessage: 'No' })) : '--';
                    break;
                case 'Recent':
                    base[s.StudentName] = cell ? (cell.recentCorrect ? formatMessage({ id: 'correct.d6100cc2', defaultMessage: 'Correct' }) : formatMessage({ id: 'incorrect.2a901341', defaultMessage: 'Incorrect' })) : '--';
                    break;
                case 'Attempts':
                    base[s.StudentName] = cell ? cell.attempts : 0;
                    break;
                case 'Time':
                    base[s.StudentName] = cell && cell.attempts > 0 ? formatSeconds(cell.totalTimeMinutes / cell.attempts) : '--';
                    break;

            }

        });
        return base;

    }), [sortedData, uniqueStudents, cellData, metricView, formatMessage]);

    const handleExportCSV = useCallback(() => {

        const reportName = reportSections[0]?.Title || 'TestProgressReport';
        const fileName = buildExportFileName('Report', reportName, 'csv');
        setState({ exportFileName: fileName });
        setState({ exportRows: viewMode === 'Student' ? buildStudentViewExport() : buildQuestionViewExport() });
        setTimeout(() => document.getElementById('csvLink73')?.click(), 0);

    }, [reportSections, viewMode, buildStudentViewExport, buildQuestionViewExport]);

    // ---- Button group configs ----
    const viewModeButtons = useMemo(() => [
        { action: () => {

            setState({ sort: [], viewMode: 'Student' });

        }, title: formatMessage({ id: 'student.4f46d2e3', defaultMessage: 'Student' }) },
        { action: () => {

            setState({ sort: [], viewMode: 'Question' });

        }, title: formatMessage({ id: 'question.e7d7defa', defaultMessage: 'Question' }) }
    ], [formatMessage]);

    const metricButtons = useMemo(() => [
        { action: () => setState({ metricView: 'Attained' }), title: formatMessage({ id: 'attained.704661b2', defaultMessage: 'Attained' }) },
        { action: () => setState({ metricView: 'Recent' }), title: formatMessage({ id: 'recent.f6fba114', defaultMessage: 'Recent' }) },
        { action: () => setState({ metricView: 'Attempts' }), title: formatMessage({ id: 'attempts.ae7c8eee', defaultMessage: 'Attempts' }) },
        { action: () => setState({ metricView: 'Time' }), title: formatMessage({ id: 'time.446a3218', defaultMessage: 'Time' }) }
    ], [formatMessage]);

    // ---- Summary cards ----
    const summaryCards = useMemo(() => [
        {
            label: formatMessage({ id: 'students_attempted.1e3bc45f', defaultMessage: 'Students Attempted' }),
            tooltip: formatMessage({ id: 'percent_of_students_who_submitted_at_lea.a709acbd', defaultMessage: 'Percent of students who submitted at least one question attempt within the selected scope' }),
            value: `${Math.round(summaryMetrics.studentsAttemptedPercent)}%`
        },
        {
            label: formatMessage({ id: 'avg_attempts_student.f3df6812', defaultMessage: 'Avg Attempts / Student' }),
            tooltip: formatMessage({ id: 'average_number_of_submitted_question_att.f349b515', defaultMessage: 'Average number of submitted question attempts across questions associated with selected Test(s)' }),
            value: Math.round(summaryMetrics.avgAttemptsPerStudent).toString()
        },
        {
            label: formatMessage({ id: 'avg_coverage.0954accd', defaultMessage: 'Avg Coverage' }),
            tooltip: formatMessage({ id: 'average_percentage_of_questions_that_stu.41799141', defaultMessage: 'Average percentage of questions that students have attempted at least once' }),
            value: `${Math.round(summaryMetrics.avgCoverage)}%`
        },
        {
            label: formatMessage({ id: 'avg_attained.8c0a67d4', defaultMessage: 'Avg Attained' }),
            tooltip: formatMessage({ id: 'average_percentage_of_questions_answered.5609839f', defaultMessage: 'Average percentage of questions answered correctly at least once per student' }),
            value: `${Math.round(summaryMetrics.avgAttained)}%`
        },
        {
            label: formatMessage({ id: 'avg_recent.b5774606', defaultMessage: 'Avg Recent' }),
            tooltip: formatMessage({ id: 'average_percentage_of_questions_where_ea.499851bf', defaultMessage: "Average percentage of questions where each student's most recent submitted attempt was correct" }),
            value: `${Math.round(summaryMetrics.avgRecent)}%`
        },
        {
            label: formatMessage({ id: 'avg_time_question.1a5ce3ba', defaultMessage: 'Avg Time / Question' }),
            tooltip: formatMessage({ id: 'average_time_spent_per_question_attempt.e2fd5d47', defaultMessage: 'Average time spent per question attempt within scope' }),
            value: formatSeconds(summaryMetrics.avgTimePerQuestionMinutes)
        }
    ], [summaryMetrics, formatMessage]);

    return (
        <>
            {/* Header Summary — Test Engagement */}
            <Container fluid className="px-0 mb-4">
                <h5 className="fw-bold mb-3">
                    <FormattedMessage defaultMessage="Test Engagement" id="test_engagement.3947ee36" />
                </h5>
                <Row className="g-3 mx-0">
                    {summaryCards.map(metric => (
                        <Col key={metric.label} lg="2" md="4" sm="4" xs="6">
                            <Card className="p-3 shadow-sm h-100 text-center">
                                <ReportToolTip id={`summary-${metric.label}`} tooltip={metric.tooltip} trigger="hover">
                                    <div className="fw-semibold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                                        {metric.label}
                                    </div>
                                    <div className="fw-bold fs-3">
                                        {metric.value}
                                    </div>
                                </ReportToolTip>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>

            {/* Table */}
            <Card>
                <CSVLink
                    className="d-none"
                    data={exportRows}
                    filename={exportFileName}
                    id="csvLink73"
                    type="button"
                >
                </CSVLink>
                <div className="p-3 d-flex justify-content-between">
                    <ViewOptionsBar buttonGroups={[viewModeButtons, metricButtons]}>
                        {objectiveOptions.length > 0 && (
                            <div className="ms-4">
                                <GMetrixDropdown
                                    filterable
                                    multiSelect
                                    dataItemKey="value"
                                    dataItems={objectiveOptions}
                                    label={formatMessage({ id: 'show_questions_for_objectives.77d12c2f', defaultMessage: 'Show Questions For Objectives' })}
                                    onChange={e => setState({ selectedObjectives: e as ObjectiveOption[] })}
                                    style={{ maxWidth: '300px' }}
                                    textField="name"
                                    value={selectedObjectives}
                                />
                            </div>
                        )}
                    </ViewOptionsBar>
                    <div className="mt-4">
                        <GMetrixButton onClick={handleExportCSV} variant="primary">
                            <FormattedMessage defaultMessage="Export to CSV" id="export_to_csv.e1de1359" />
                        </GMetrixButton>
                    </div>
                </div>

                {/* ---- STUDENT VIEW ---- */}
                {viewMode === 'Student' ? (
                    <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                        <Table bordered hover className="kendo-table sticky-table-header mb-0">
                            <thead>
                                <tr>
                                    <th
                                        className="text-start"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'StudentName' }] })}
                                        style={{ cursor: 'pointer', left: 0, minWidth: 150, position: 'sticky', zIndex: 2 }}
                                    >
                                        {sortableHeader(
                                            <span className="d-flex justify-content-start">
                                                <FormattedMessage defaultMessage="Student Name" id="student_name.f4347b21" />
                                            </span>,
                                            'StudentName',
                                            sort
                                        )}
                                    </th>
                                    {showClassroomColumn && (
                                        <th
                                            className="text-center"
                                            onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'Classroom' }] })}
                                            style={{ cursor: 'pointer', minWidth: 130 }}
                                        >
                                            {sortableHeader(
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Classroom" id="classroom.127e40cb" />
                                                </span>,
                                                'Classroom',
                                                sort
                                            )}
                                        </th>
                                    )}
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'coverage' }] })}
                                        style={{ cursor: 'pointer', minWidth: 150 }}
                                    >
                                        {sortableHeader(
                                            <ReportToolTip id="col-coverage" tooltip={formatMessage({ id: 'percentage_of_questions_attempted_at_lea.af8e7244', defaultMessage: 'Percentage of questions attempted at least once by the student' })} trigger="hover">
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Question Coverage" id="question_coverage.47d009b6" />
                                                </span>
                                            </ReportToolTip>,
                                            'coverage',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'attained' }] })}
                                        style={{ cursor: 'pointer', minWidth: 130 }}
                                    >
                                        {sortableHeader(
                                            <ReportToolTip id="col-attained" tooltip={formatMessage({ id: 'percentage_of_questions_answered_correct.ba49ce93', defaultMessage: 'Percentage of questions answered correctly at least once by this student' })} trigger="hover">
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Attained" id="attained.704661b2" />
                                                </span>
                                            </ReportToolTip>,
                                            'attained',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'recent' }] })}
                                        style={{ cursor: 'pointer', minWidth: 130 }}
                                    >
                                        {sortableHeader(
                                            <ReportToolTip id="col-recent" tooltip={formatMessage({ id: 'percentage_of_questions_answered_correct.cad8ad90', defaultMessage: "Percentage of questions answered correctly during this student's most recent attempt" })} trigger="hover">
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Recent" id="recent.f6fba114" />
                                                </span>
                                            </ReportToolTip>,
                                            'recent',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'avgAttemptsPerQuestion' }] })}
                                        style={{ cursor: 'pointer', minWidth: 180 }}
                                    >
                                        {sortableHeader(
                                            <ReportToolTip id="col-avgAttempts" tooltip={formatMessage({ id: 'average_number_of_submitted_attempts_per.85b24416', defaultMessage: 'Average number of submitted attempts per question for this student within the selected scope' })} trigger="hover">
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Avg Attempts / Question" id="avg_attempts_question.64637c60" />
                                                </span>
                                            </ReportToolTip>,
                                            'avgAttemptsPerQuestion',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'avgTimePerQuestionMinutes' }] })}
                                        style={{ cursor: 'pointer', minWidth: 170 }}
                                    >
                                        {sortableHeader(
                                            <ReportToolTip id="col-avgTime" tooltip={formatMessage({ id: 'average_time_spent_per_question_attempt.313a1477', defaultMessage: 'Average time spent per question attempt by this student within scope' })} trigger="hover">
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Avg Time / Question" id="avg_time_question.1a5ce3ba" />
                                                </span>
                                            </ReportToolTip>,
                                            'avgTimePerQuestionMinutes',
                                            sort
                                        )}
                                    </th>
                                    {/* Dynamic question columns */}
                                    {filteredQuestions.map(q => (
                                        <th
                                            className="text-center"
                                            key={q.QuestionId}
                                            onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: `question-${q.QuestionId}` }] })}
                                            style={{ cursor: 'pointer', minWidth: EXPANDED_COLUMN_WIDTH }}
                                        >
                                            {sortableHeader(
                                                <ReportToolTip copyText={q.displayName} id={`qh-${q.QuestionId}`} tooltip={q.displayName} trigger="hover">
                                                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {q.displayName}
                                                    </span>
                                                </ReportToolTip>,
                                                `question-${q.QuestionId}`,
                                                sort
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {displayData.map((student: any, rowIndex: number) => (
                                    <tr key={`${student.StudentProfileId}-${rowIndex}`}>
                                        <td style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                            <span
                                                onClick={() => navigateToStudentDetail(student.StudentProfileId)}
                                                style={{ color: '#0066cc', cursor: 'pointer', textDecoration: 'underline' }}
                                                title={formatMessage({ id: 'view_student_test_details.72007479', defaultMessage: 'View student test details' })}
                                            >
                                                {student.StudentName}
                                            </span>
                                        </td>
                                        {showClassroomColumn && (
                                            <td className="text-center">
                                                {student.Classroom || '---'}
                                            </td>
                                        )}
                                        <td className="text-center">
                                            {Math.round(student.coverage)}
                                            %
                                        </td>
                                        <td className="text-center">
                                            {Math.round(student.attained)}
                                            %
                                        </td>
                                        <td className="text-center">
                                            {Math.round(student.recent)}
                                            %
                                        </td>
                                        <td className="text-center">
                                            {student.avgAttemptsPerQuestion.toFixed(1)}
                                        </td>
                                        <td className="text-center">
                                            {formatSeconds(student.avgTimePerQuestionMinutes)}
                                        </td>
                                        {/* Dynamic question cells */}
                                        {filteredQuestions.map(q => (
                                            <td className="text-center" key={q.QuestionId}>
                                                <ReportToolTip
                                                    id={`cell-${student.StudentProfileId}-${q.QuestionId}`}
                                                    tooltip={getCellTooltip(student.StudentProfileId, q.QuestionId)}
                                                >
                                                    {getCellValue(student.StudentProfileId, q.QuestionId)}
                                                </ReportToolTip>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : (

                    /* ---- QUESTION VIEW ---- */
                    <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                        <Table bordered hover className="kendo-table sticky-table-header mb-0">
                            <thead>
                                <tr>
                                    <th
                                        className="text-start"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'displayName' }] })}
                                        style={{ cursor: 'pointer', left: 0, minWidth: 280, position: 'sticky', zIndex: 2 }}
                                    >
                                        {sortableHeader(
                                            <span className="d-flex justify-content-start">
                                                <FormattedMessage defaultMessage="Question" id="question.e7d7defa" />
                                            </span>,
                                            'displayName',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'studentCoverage' }] })}
                                        style={{ cursor: 'pointer', minWidth: 150 }}
                                    >
                                        {sortableHeader(
                                            <ReportToolTip id="col-studentCov" tooltip={formatMessage({ id: 'percent_of_students_in_scope_who_attempt.79f34a30', defaultMessage: 'Percent of students in scope who attempted this question at least once' })} trigger="hover">
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Student Coverage" id="student_coverage.92274580" />
                                                </span>
                                            </ReportToolTip>,
                                            'studentCoverage',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'attained' }] })}
                                        style={{ cursor: 'pointer', minWidth: 130 }}
                                    >
                                        {sortableHeader(
                                            <ReportToolTip id="col-q-attained" tooltip={formatMessage({ id: 'percent_of_students_who_answered_this_qu.0d237eb9', defaultMessage: 'Percent of students who answered this question correctly at least once' })} trigger="hover">
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Attained" id="attained.704661b2" />
                                                </span>
                                            </ReportToolTip>,
                                            'attained',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'recent' }] })}
                                        style={{ cursor: 'pointer', minWidth: 130 }}
                                    >
                                        {sortableHeader(
                                            <ReportToolTip id="col-q-recent" tooltip={formatMessage({ id: 'percentage_of_students_who_answered_this.040c7930', defaultMessage: 'Percentage of students who answered this question correctly during their most recent attempt' })} trigger="hover">
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Recent" id="recent.f6fba114" />
                                                </span>
                                            </ReportToolTip>,
                                            'recent',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'avgAttemptsPerStudent' }] })}
                                        style={{ cursor: 'pointer', minWidth: 180 }}
                                    >
                                        {sortableHeader(
                                            <ReportToolTip id="col-q-avgAttempts" tooltip={formatMessage({ id: 'average_number_of_attempts_per_student_a.660315ef', defaultMessage: 'Average number of attempts per student among those who attempted this question' })} trigger="hover">
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Avg Attempts / Student" id="avg_attempts_student.f3df6812" />
                                                </span>
                                            </ReportToolTip>,
                                            'avgAttemptsPerStudent',
                                            sort
                                        )}
                                    </th>
                                    <th
                                        className="text-center"
                                        onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'avgTimePerAttemptMinutes' }] })}
                                        style={{ cursor: 'pointer', minWidth: 170 }}
                                    >
                                        {sortableHeader(
                                            <ReportToolTip id="col-q-avgTime" tooltip={formatMessage({ id: 'average_time_spent_per_attempt_on_this_q.f01153b4', defaultMessage: 'Average time spent per attempt on this question within scope' })} trigger="hover">
                                                <span className="d-flex justify-content-center">
                                                    <FormattedMessage defaultMessage="Avg Time / Attempt" id="avg_time_attempt.c6fd1ca6" />
                                                </span>
                                            </ReportToolTip>,
                                            'avgTimePerAttemptMinutes',
                                            sort
                                        )}
                                    </th>
                                    {/* Dynamic student columns */}
                                    {uniqueStudents.map(s => (
                                        <th
                                            className="text-center"
                                            key={s.StudentProfileId}
                                            onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: `student-${s.StudentProfileId}` }] })}
                                            style={{ cursor: 'pointer', minWidth: EXPANDED_COLUMN_WIDTH }}
                                        >
                                            {sortableHeader(
                                                <ReportToolTip id={`sh-${s.StudentProfileId}`} tooltip={s.StudentName} trigger="hover">
                                                    <span
                                                        onClick={() => navigateToStudentDetail(s.StudentProfileId)}
                                                        style={{ color: '#0066cc', cursor: 'pointer', display: 'block', overflow: 'hidden', textDecoration: 'underline', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    >
                                                        {s.StudentName}
                                                    </span>
                                                </ReportToolTip>,
                                                `student-${s.StudentProfileId}`,
                                                sort
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {displayData.map((question: any, rowIndex: number) => (
                                    <tr key={`${question.QuestionId}-${rowIndex}`}>
                                        <td style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                            <ReportToolTip copyText={question.displayName} id={`q-${question.QuestionId}`} tooltip={question.displayName} trigger="hover">
                                                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {question.displayName}
                                                </span>
                                            </ReportToolTip>
                                        </td>
                                        <td className="text-center">
                                            {Math.round(question.studentCoverage)}
                                            %
                                        </td>
                                        <td className="text-center">
                                            {Math.round(question.attained)}
                                            %
                                        </td>
                                        <td className="text-center">
                                            {Math.round(question.recent)}
                                            %
                                        </td>
                                        <td className="text-center">
                                            {question.avgAttemptsPerStudent.toFixed(1)}
                                        </td>
                                        <td className="text-center">
                                            {formatSeconds(question.avgTimePerAttemptMinutes)}
                                        </td>
                                        {/* Dynamic student cells */}
                                        {uniqueStudents.map(s => (
                                            <td className="text-center" key={s.StudentProfileId}>
                                                <ReportToolTip
                                                    id={`cell-${question.QuestionId}-${s.StudentProfileId}`}
                                                    tooltip={getCellTooltip(s.StudentProfileId, question.QuestionId)}
                                                >
                                                    {getCellValue(s.StudentProfileId, question.QuestionId)}
                                                </ReportToolTip>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Card>
        </>
    );

};